import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Spin, Table, Button, Tag, Modal } from "@/frontend/components/common";
import { FaCalendarAlt, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { fixtureService, fixtureStatusService, tournamentFormatService, matchTeamService, matchScoreService } from "@/frontend/services";
import type {
  FixtureResponseDTO,
  FixtureStatusResponseDTO,
  // PaginationRequestDTO,
} from "@/shared/dto";
import type { ColumnsType } from "antd/es/table";
import type { SeasonInfo, TournamentStageInfo } from "../types";
import {
  FixtureCreateModal,
  FixtureEditModal,
  FixtureImportModal,
  TournamentAutomationModal,
} from "../shared";

interface FixturesTabProps {
  tournamentId: string;
  currentSeason?: SeasonInfo;
  tournamentStages: TournamentStageInfo[];
  requiresFairPlay: boolean;
  autoCategories?: string[];
}

export function FixturesTab({
  tournamentId,
  currentSeason,
  tournamentStages,
  requiresFairPlay,
  autoCategories = [],
}: FixturesTabProps) {
  const [fixtures, setFixtures] = useState<FixtureResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [nextCursor, setNextCursor] = useState<{
    cursorDate: string | null;
    cursorId: string;
  } | null>(null);
  const [prevCursor, setPrevCursor] = useState<{
    cursorDate: string | null;
    cursorId: string;
  } | null>(null);
  const [fixtureStatuses, setFixtureStatuses] = useState<FixtureStatusResponseDTO[]>([]);
  
  // Estado del modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFixture, setEditingFixture] = useState<FixtureResponseDTO | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [automationModalOpen, setAutomationModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchFixtures = useCallback(async () => {
    if (!currentSeason?.id) return;

    setLoading(true);
    try {
      const response = await fixtureService.getBySeasonId(currentSeason.id, {
        pageSize,
        pastCount: 5,
        initialLoad: true,
      });

      if (response.success && response.data) {
        setFixtures(response.data.data || []);
        setNextCursor(response.data.pagination?.nextCursor || null);
        setPrevCursor(response.data.pagination?.prevCursor || null);
      }
    } catch (error) {
      console.error("Error al cargar partidos:", error);
    } finally {
      setLoading(false);
    }
  }, [currentSeason?.id, pageSize]);

  const handleNextPage = useCallback(async () => {
    if (!currentSeason?.id || !nextCursor) return;

    setLoading(true);
    try {
      const response = await fixtureService.getBySeasonId(currentSeason.id, {
        pageSize,
        direction: "forward",
        cursorDate: nextCursor.cursorDate,
        cursorId: nextCursor.cursorId,
      });

      if (response.success && response.data) {
        setFixtures(response.data.data || []);
        setNextCursor(response.data.pagination?.nextCursor || null);
        setPrevCursor(response.data.pagination?.prevCursor || null);
      }
    } catch (error) {
      console.error("Error al cargar siguiente página de partidos:", error);
    } finally {
      setLoading(false);
    }
  }, [currentSeason?.id, nextCursor, pageSize]);

  const handlePrevPage = useCallback(async () => {
    if (!currentSeason?.id || !prevCursor) return;

    setLoading(true);
    try {
      const response = await fixtureService.getBySeasonId(currentSeason.id, {
        pageSize,
        direction: "backward",
        cursorDate: prevCursor.cursorDate,
        cursorId: prevCursor.cursorId,
      });

      if (response.success && response.data) {
        setFixtures(response.data.data || []);
        setNextCursor(response.data.pagination?.nextCursor || null);
        setPrevCursor(response.data.pagination?.prevCursor || null);
      }
    } catch (error) {
      console.error("Error al cargar página anterior de partidos:", error);
    } finally {
      setLoading(false);
    }
  }, [currentSeason?.id, prevCursor, pageSize]);

  useEffect(() => {
    fetchFixtures();
  }, [fetchFixtures]);

  useEffect(() => {
    if (!currentSeason?.id || fixtureStatuses.length === 0) return;

    const seasonId = currentSeason.id;
    let cancelled = false;

    const normalizeName = (name: string | undefined | null) =>
      String(name ?? "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\b(fc|cf|club|deportivo|atletico|real|sd|ud|afc)\b/g, "")
        .replace(/[^a-z0-9]/g, "");

    const dateKey = (value: string | Date | undefined | null) => {
      if (!value) return null;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    };

    async function syncCatalogScores() {
      const finalStatus = fixtureStatuses.find((s) => s.isFinal);
      if (!finalStatus) return;
      const finalStatusIds = new Set(
        fixtureStatuses.filter((s) => s.isFinal).map((s) => s.id),
      );

      const [formatsRes, teamsRes, fixturesRes] = await Promise.all([
        tournamentFormatService.getPaginate({ page: 1, pageSize: 500 }),
        matchTeamService.getPaginate({ page: 1, pageSize: 500 }),
        fixtureService.getBySeasonId(seasonId, {
          pageSize: 5000,
          direction: "forward",
        }),
      ]);

      if (
        cancelled ||
        !formatsRes.success ||
        !formatsRes.data ||
        !teamsRes.success ||
        !teamsRes.data ||
        !fixturesRes.success ||
        !fixturesRes.data
      ) {
        return;
      }

      const allTeams: any[] = teamsRes.data.data ?? [];
      const teamById = new Map(allTeams.map((team: any) => [team.id, team]));
      const seasonFixtures: any[] = fixturesRes.data.data ?? [];
      const formatsWithMatches = formatsRes.data.data.filter(
        (format: any) => Array.isArray((format.config as any)?.matches),
      );

      const scoreResults = await Promise.all(
        formatsWithMatches.map((format: any) =>
          matchScoreService.getByEvent(format.id).then((res) => ({
            format,
            scores: res.success && res.data ? (res.data as any[]) : [],
          })),
        ),
      );

      let updatedAny = false;

      for (const { format, scores } of scoreResults) {
        if (scores.length === 0) continue;

        const matches: any[] = (format.config as any)?.matches ?? [];
        for (const match of matches) {
          // Solo se sincronizan partidos que el catálogo da por terminados;
          // un partido en vivo o programado no debe finalizar el fixture.
          if (match.status && match.status !== "finished") continue;

          const matchOrder = Number(match.order ?? matches.indexOf(match) + 1);
          const score = scores.find((s: any) => Number(s.matchOrder) === matchOrder);
          if (!score) continue;

          const homeName = teamById.get(match.homeTeamId)?.name;
          const awayName = teamById.get(match.awayTeamId)?.name;
          const matchDateKey = dateKey(match.date ?? match.time);
          if (!homeName || !awayName || !matchDateKey) continue;

          const fixture = seasonFixtures.find((fx: any) => {
            return (
              normalizeName(fx.homeTeam?.name) === normalizeName(homeName) &&
              normalizeName(fx.awayTeam?.name) === normalizeName(awayName) &&
              dateKey(fx.matchDate) === matchDateKey
            );
          });
          if (!fixture) continue;

          // Un fixture ya finalizado solo se corrige manualmente (Editar);
          // esta sincronización no debe pisar resultados confirmados.
          if (finalStatusIds.has(fixture.statusId ?? "")) continue;

          const nextHomeScore = Number(score.homeScore ?? 0);
          const nextAwayScore = Number(score.awayScore ?? 0);
          const shouldUpdateScore =
            Number(fixture.homeScore ?? 0) !== nextHomeScore ||
            Number(fixture.awayScore ?? 0) !== nextAwayScore;
          const shouldUpdateStatus = fixture.statusId !== finalStatus.id;

          if (!shouldUpdateScore && !shouldUpdateStatus) continue;

          const updateRes = await fixtureService.update(fixture.id, {
            homeScore: nextHomeScore,
            awayScore: nextAwayScore,
            statusId: finalStatus.id,
            updateStandings: true,
          });

          if (updateRes.success) {
            fixture.homeScore = nextHomeScore;
            fixture.awayScore = nextAwayScore;
            fixture.statusId = finalStatus.id;
            updatedAny = true;
          }
        }
      }

      if (!cancelled && updatedAny) {
        fetchFixtures();
      }
    }

    syncCatalogScores().catch((error) => {
      console.error("Error sincronizando scores del catalogo:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [currentSeason?.id, fixtureStatuses, fetchFixtures]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const resp = await fixtureStatusService.getPaginate({ page: 1, pageSize: 50 });
        if (resp.success && resp.data) {
          setFixtureStatuses(resp.data.data || []);
        }
      } catch (e) {
        console.error("Error al cargar estados de partido:", e);
      }
    };
    fetchStatuses();
  }, []);

  const handleEdit = (fixture: FixtureResponseDTO) => {
    setEditingFixture(fixture);
    setEditModalOpen(true);
  };

  const handleModalSuccess = () => {
    setEditModalOpen(false);
    setEditingFixture(null);
    fetchFixtures();
  };

  const handleModalCancel = () => {
    setEditModalOpen(false);
    setEditingFixture(null);
  };

  const handleDelete = useCallback(async (fixture: FixtureResponseDTO) => {
    Modal.confirm({
      title: "Eliminar partido",
      content: `¿Estás seguro de eliminar el partido ${fixture.homeTeam?.name ?? "Local"} vs ${fixture.awayTeam?.name ?? "Visitante"}?`,
      okText: "Eliminar",
      cancelText: "Cancelar",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await fixtureService.delete(fixture.id);
          if (response.success) {
            fetchFixtures();
          } else {
            Modal.error({
              title: "Error",
              content: (response as any).error ?? "No se pudo eliminar el partido",
            });
          }
        } catch (error) {
          Modal.error({
            title: "Error",
            content: "No se pudo eliminar el partido",
          });
        }
      },
    });
  }, [fetchFixtures]);

  const handleImportSuccess = () => {
    setImportModalOpen(false);
    fetchFixtures();
  };

  const handleCreateSuccess = () => {
    setCreateModalOpen(false);
    fetchFixtures();
  };

  const statusMap = useMemo(() => {
    const m = new Map<string, FixtureStatusResponseDTO>();
    fixtureStatuses.forEach((s) => m.set(s.id, s));
    return m;
  }, [fixtureStatuses]);

  const columns: ColumnsType<FixtureResponseDTO> = [
    {
      title: "Equipo Local",
      dataIndex: "homeTeam",
      key: "homeTeam",
      render: (_, record) => (
        <div className="text-center font-medium flex items-center justify-center gap-1">
          {record.homeTeam?.country?.flag && (
            <img
              src={record.homeTeam.country.flag}
              alt=""
              className="w-4 h-3 object-cover rounded"
            />
          )}
          {record.homeTeam ? record.homeTeam.name : "-"}
        </div>
      ),
    },
    {
      title: "Resultado",
      key: "result",
      width: 120,
      align: "center",
      render: (_, record) => {
        const homeScore = record.homeScore != null ? record.homeScore : "-";
        const awayScore = record.awayScore != null ? record.awayScore : "-";
        return (
          <div className="flex items-center justify-center gap-2">
            <span className="font-bold text-lg">
              {statusMap.get(record.statusId ?? "")?.isFinal
                ? `${homeScore} - ${awayScore}`
                : "-"}
            </span>
            {(record.homePenalties != null && record.homePenalties > 0) ||
            (record.awayPenalties != null && record.awayPenalties > 0) ? (
              <span className="text-sm text-gray-500">
                ({record.homePenalties ?? 0} - {record.awayPenalties ?? 0} penales)
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Equipo Visitante",
      dataIndex: "awayTeam",
      key: "awayTeam",
      render: (_, record) => (
        <div className="text-center font-medium flex items-center justify-center gap-1">
          {record.awayTeam?.country?.flag && (
            <img
              src={record.awayTeam.country.flag}
              alt=""
              className="w-4 h-3 object-cover rounded"
            />
          )}
          {record.awayTeam ? record.awayTeam.name : "-"}
        </div>
      ),
    },
    {
      title: "Fecha",
      dataIndex: "matchDate",
      key: "matchDate",
      width: 150,
      render: (date: Date) => {
        try {
          const d = new Date(date);
          return d.toLocaleDateString("es-ES", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        } catch {
          return "-";
        }
      },
    },
    {
      title: "Estado",
      dataIndex: "statusId",
      key: "statusId",
      width: 120,
      align: "center",
      render: (statusId: string | null) => {
        if (!statusId) return <span className="text-gray-400">Programado</span>;
        const status = statusMap.get(statusId);
        return <Tag color="blue">{status?.name || statusId}</Tag>;
      },
    },
    {
      title: "Acciones",
      key: "actions",
      width: 200,
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          {record.homeTeam && record.awayTeam && (
            <Button
              type="link"
              icon={<FaEdit />}
              onClick={() => handleEdit(record)}
              title="Editar partido"
            >
              Editar
            </Button>
          )}
          <Button
            type="link"
            danger
            icon={<FaTrash />}
            onClick={() => handleDelete(record)}
            title="Eliminar partido"
          >
            Eliminar
          </Button>
        </div>
      ),
    },
  ];

  if (!currentSeason) {
    return (
      <Card className="shadow-sm">
        <div className="text-center py-12">
          <FaCalendarAlt className="mx-auto text-gray-300 text-5xl mb-4" />
          <p className="text-gray-500">
            No hay una temporada actual seleccionada
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="py-4">
      <Card className="shadow-sm">
        <div className="flex justify-end gap-2 mb-4">
          <Button type="primary" onClick={() => setAutomationModalOpen(true)}>
            Automatizar Torneos
          </Button>
          <Button
            type="primary"
            icon={<FaPlus />}
            onClick={() => setCreateModalOpen(true)}
          >
            Agregar Partido
          </Button>
          <Button
            icon={<FaCalendarAlt />}
            onClick={() => setImportModalOpen(true)}
          >
            Importar desde Catálogo
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" tip="Cargando partidos..." />
          </div>
        ) : fixtures.length > 0 ? (
          <>
            <Table
              columns={columns}
              dataSource={fixtures}
              rowKey="id"
              size="middle"
              pagination={false}
            />

            <div className="flex justify-between items-center mt-4">
              <Button
                onClick={handlePrevPage}
                disabled={!prevCursor || loading}
              >
                Anterior
              </Button>

              <div className="text-sm text-gray-500">
                {fixtures.length} partidos cargados
              </div>

              <Button
                onClick={handleNextPage}
                disabled={!nextCursor || loading}
              >
                Siguiente
              </Button>
            </div>
          </>       
        ) : (
          <div className="text-center py-12">
            <FaCalendarAlt className="mx-auto text-gray-300 text-5xl mb-4" />
            <p className="text-gray-500">
              No hay partidos disponibles para esta temporada
            </p>
          </div>
        )}
      </Card>

      <FixtureEditModal
        open={editModalOpen}
        fixture={editingFixture}
        fixtureStatuses={fixtureStatuses}
        requiresFairPlay={requiresFairPlay}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
      />

      {currentSeason && (
        <FixtureImportModal
          open={importModalOpen}
          tournamentId={tournamentId}
          seasonId={currentSeason.id}
          tournamentStages={tournamentStages}
          currentStageOrder={currentSeason.currentStageOrder}
          onCancel={() => setImportModalOpen(false)}
          onSuccess={handleImportSuccess}
        />
      )}

      <TournamentAutomationModal
        open={automationModalOpen}
        tournamentId={tournamentId}
        autoCategories={autoCategories}
        onCancel={() => setAutomationModalOpen(false)}
        onSuccess={() => setAutomationModalOpen(false)}
      />
      {currentSeason && (
        <FixtureCreateModal
          open={createModalOpen}
          seasonId={currentSeason.id}
          onCancel={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}
